import axios from 'axios';
import envConfig from './envConfig';

// Utility function for base64 encoding that works in both Node.js and browser environments
const toBase64 = (str) => {
  if (typeof window === 'undefined') {
    // Node.js environment
    try {
      return Buffer.from(str).toString('base64');
    } catch (e) {
      console.error('Error encoding with Buffer:', e);
      // Fallback to a simple implementation for build-time
      const safeEncode = (s) => {
        return 'base64_encoded_' + s;  // Safe fallback that won't crash builds
      };
      return safeEncode(str);
    }
  } else {
    // Browser environment
    return window.btoa(str);
  }
};

// Listmonk API client
const createListmonkClient = () => {
  // Get environment variables from our explicit config
  const baseURL = envConfig.LISTMONK_URL || '';
  const apiKey = envConfig.LISTMONK_PASSWORD || '';
  const username = envConfig.LISTMONK_USERNAME || '';

  // In production, ListMonk is running at mail.sierraoutdoors.org
  // The API is accessible at mail.sierraoutdoors.org/api
  const isBrowser = typeof window !== 'undefined';
  
  // Always use the actual ListMonk URL in production
  // In development, use the proxy but without /api prefix since the proxy handles that
  const finalBaseURL = process.env.NODE_ENV === 'production'
    ? 'https://mail.sierraoutdoors.org/api'
    : '/api/listmonk-proxy';
    
  // Log the configuration
  console.log('🔗 [LISTMONK] Configuration:', {
    environment: process.env.NODE_ENV,
    baseURL: finalBaseURL,
    hasAuth: Boolean(username && apiKey),
    welcomeListId: envConfig.LISTMONK_WELCOME_LIST_ID,
    welcomeTemplateId: envConfig.LISTMONK_WELCOME_TEMPLATE_ID
  });

  // Log available config for debugging (will be visible in browser console)
  console.log('Listmonk config:', {
    baseURL: baseURL ? `${baseURL.substring(0, 8)}...` : 'Not set',
    hasUsername: Boolean(username),
    hasApiKey: Boolean(apiKey),
    useProxy: isBrowser,
    finalBaseURL: finalBaseURL,
    isBrowser,
    environment: process.env.NODE_ENV || 'unknown'
  });

  // Production debugging: Test common URL paths if we're in production
  // This only runs once at initialization and can help identify URL path issues
  if (process.env.NODE_ENV === 'production' && baseURL) {
    console.log('PRODUCTION DEBUG: Testing ListMonk URL paths');
    const testCommonPaths = async () => {
      // Common paths where ListMonk might be installed
      const paths = [
        '/api/lists',       // Standard path
      ];
      
      for (const path of paths) {
        try {
          const testUrl = baseURL.endsWith('/') 
            ? `${baseURL}${path.startsWith('/') ? path.substring(1) : path}`
            : `${baseURL}${path}`;
            
          console.log(`PRODUCTION DEBUG: Testing path ${testUrl}`);
          
          // Create a test client with auth
          const testClient = axios.create({
            timeout: 5000,
            headers: {
              'Content-Type': 'application/json'
            },
            auth: {
              username,
              password: apiKey
            }
          });
          
          const response = await testClient.get(testUrl, { 
            validateStatus: () => true // Accept any status to check if endpoint exists
          });
          
          console.log(`PRODUCTION DEBUG: Path ${path} response:`, {
            status: response.status,
            isValid: response.status === 200,
            dataType: response.headers?.['content-type']
          });
          
          // If we found a working path that's not our configured path, log it clearly
          if (response.status === 200 && !baseURL.includes(path.split('/api')[0])) {
            console.warn(`PRODUCTION DEBUG: Found working ListMonk path at ${path} but your configured URL doesn't include this path!`);
            console.warn(`PRODUCTION DEBUG: Consider updating GATSBY_LISTMONK_URL to include the correct path`);
          }
        } catch (error) {
          console.log(`PRODUCTION DEBUG: Path ${path} error:`, {
            message: error.message,
            code: error.code
          });
        }
      }
    };
    
    // Don't block initialization - run tests in background
    testCommonPaths().catch(err => {
      console.error('PRODUCTION DEBUG: Error testing paths:', err.message);
    });
  }

  // Create axios instance with environment-specific configuration
  const client = axios.create({
    baseURL: finalBaseURL,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    timeout: 10000,
    validateStatus: (status) => status >= 200 && status < 500
  });

  // Only add authentication in production
  if (process.env.NODE_ENV === 'production') {
    // Add both auth property and Authorization header for robust authentication
    client.defaults.auth = {
      username: username,
      password: apiKey
    };
    client.defaults.headers.common['Authorization'] = `Basic ${toBase64(`${username}:${apiKey}`)}`;    
  }

  // Log initial configuration
  console.log('�� [LISTMONK] Initial Config:', {
    baseURL: finalBaseURL,
    hasAuth: Boolean(username && apiKey),
    username,
    environment: process.env.NODE_ENV,
    welcomeListId: envConfig.LISTMONK_WELCOME_LIST_ID,
    welcomeTemplateId: envConfig.LISTMONK_WELCOME_TEMPLATE_ID
  });

  // Log initial configuration
  console.log('🔧 [LISTMONK] Initial Config:', {
    baseURL: finalBaseURL,
    hasAuth: Boolean(username && apiKey),
    username,
    environment: process.env.NODE_ENV
  });

  // Add request interceptor to ensure correct API paths
  client.interceptors.request.use(config => {
    // In production, we already have /api in the baseURL
    // In development, the proxy handles the /api prefix
    // So we just need to ensure no duplicate /api
    if (config.url.startsWith('/api/') || config.url.startsWith('api/')) {
      config.url = config.url.replace(/^\/?api\//, '');
    }
    
    // Log the request details
    const fullUrl = `${config.baseURL}/${config.url}`;
    console.log('🔗 [LISTMONK] Request:', {
      method: config.method,
      url: fullUrl,
      hasAuth: Boolean(config.auth?.username && config.auth?.password),
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      data: config.data ? JSON.stringify(config.data).substring(0, 100) : null
    });
    
    return config;
  });

  // Add response interceptor for better error handling
  client.interceptors.response.use(
    response => {
      console.log('✅ [LISTMONK] Response:', {
        status: response.status,
        url: response.config.url,
        method: response.config.method,
        timestamp: new Date().toISOString()
      });
      return response;
    },
    error => {
      console.error('❌ [LISTMONK] Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        timestamp: new Date().toISOString()
      });

      if (error.response?.status === 401) {
        error.message = 'ListMonk authentication failed. Check your credentials.';
      } else if (error.response?.status === 404) {
        error.message = 'ListMonk API endpoint not found. Check your ListMonk URL configuration.';
      } else if (!error.response && error.code === 'ECONNREFUSED') {
        error.message = 'Could not connect to ListMonk server. Check if the service is running.';
      }

      return Promise.reject(error);
    }
  );

  // Add request interceptor for List 3 diagnostics
  client.interceptors.request.use(config => {
    try {
      // Safely check if this is a List 3 operation
      const lists = config.data?.lists;
      const url = config.url || '';
      
      const isList3Operation = (
        (Array.isArray(lists) && lists.includes(3)) ||
        url.includes('/lists/3') ||
        url.includes('lists=3')
      );

      if (isList3Operation) {
        console.log('🎯 [LIST 3 OPERATION] Request:', {
          method: config.method,
          url: config.url,
          hasAuth: !!config.auth,
          timestamp: new Date().toISOString(),
          environment: process.env.NODE_ENV
        });
      }
    } catch (err) {
      // Don't let diagnostic code break the request
      console.log('ℹ️ [LIST 3 DIAGNOSTIC] Skipped due to:', err.message);
    }
    return config;
  });

  // Add response interceptor for List 3 diagnostics
  client.interceptors.response.use(
    response => {
      try {
        // Safely check if this was a List 3 operation
        const lists = response.config.data?.lists;
        const url = response.config.url || '';
        
        const isList3Operation = (
          (Array.isArray(lists) && lists.includes(3)) ||
          url.includes('/lists/3') ||
          url.includes('lists=3')
        );

        if (isList3Operation) {
          console.log('✅ [LIST 3 OPERATION] Success:', {
            status: response.status,
            url: response.config.url,
            hasData: !!response.data,
            timestamp: new Date().toISOString()
          });
        }
      } catch (err) {
        // Don't let diagnostic code break the response
        console.log('ℹ️ [LIST 3 DIAGNOSTIC] Skipped due to:', err.message);
      }
      return response;
    },
    error => {
      // Check if this was a List 3 operation
      const isList3Operation = (
        error.config?.data?.lists?.includes(3) ||
        error.config?.url?.includes('/lists/3') ||
        error.config?.url?.includes('lists=3')
      );

      if (isList3Operation) {
        console.error('❌ [LIST 3 OPERATION] Failed:', {
          url: error.config?.url,
          status: error.response?.status,
          message: error.message,
          timestamp: new Date().toISOString(),
          response: error.response?.data
        });
      }
      return Promise.reject(error);
    }
  );

  // Add request interceptor for logging
  client.interceptors.request.use(config => {
    console.log('Listmonk API Request:', {
      method: config.method,
      url: `${config.baseURL}${config.url}`,
      hasAuth: Boolean(config.headers.Authorization),
      params: config.params,
      timeout: config.timeout
    });
    
    // Ensure we're not logging sensitive data
    const sanitizedData = config.data ? JSON.parse(JSON.stringify(config.data)) : {};
    if (sanitizedData.subscriber_emails) {
      sanitizedData.subscriber_emails = sanitizedData.subscriber_emails.map(email => 
        typeof email === 'string' ? `${email.substring(0, 3)}...` : email
      );
    }
    if (sanitizedData.data?.subscriber_email) {
      sanitizedData.data.subscriber_email = `${sanitizedData.data.subscriber_email.substring(0, 3)}...`;
    }
    console.log('Request data:', sanitizedData);
    return config;
  }, error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  });

  // Add response interceptor for debugging
  client.interceptors.response.use(response => {
    // Create a debug log with sanitized response data
    const debugData = {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      endpoint: response.config.url,
      method: response.config.method,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      baseURL: finalBaseURL
    };
    
    // Add data but sanitize it to protect sensitive information
    if (response.data) {
      // Clone data to avoid modifying the actual response
      const sanitizedData = JSON.parse(JSON.stringify(response.data));
      
      // Handle common ListMonk response structures
      if (sanitizedData.data) {
        if (Array.isArray(sanitizedData.data)) {
          debugData.dataCount = sanitizedData.data.length;
          debugData.dataType = 'array';
          debugData.firstItem = sanitizedData.data[0] ? 
            Object.keys(sanitizedData.data[0]).join(', ') : 'empty';
        } else {
          debugData.dataType = 'object';
          debugData.dataKeys = Object.keys(sanitizedData.data).join(', ');
        }
        
        // Sanitize subscriber data if present
        if (sanitizedData.data.email) {
          sanitizedData.data.email = `${sanitizedData.data.email.substring(0, 3)}...`;
        }
      }
      
      debugData.responseData = sanitizedData;
    }
    
    console.log('🟢 ListMonk API Response Success:', debugData);
    return response;
  }, error => {
    // Create a debug log with error details
    const debugData = {
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      baseURL: finalBaseURL
    };
    
    // Add request context
    if (error.config) {
      debugData.endpoint = error.config.url;
      debugData.method = error.config.method;
      debugData.requestData = error.config.data ? JSON.parse(error.config.data) : null;
    }
    
    // Add response details if available
    if (error.response) {
      debugData.status = error.response.status;
      debugData.statusText = error.response.statusText;
      debugData.headers = error.response.headers;
      debugData.data = error.response.data;
    } else {
      debugData.networkError = error.message;
      debugData.code = error.code;
    }
    
    console.error('🔴 ListMonk API Response Error:', debugData);
    return Promise.reject(error);
  });

  /**
   * Helper function to add subscriber to List 3 and send welcome email
   * @param {string} email - User's email address
   * @param {Object} attributes - Additional user attributes (optional)
   * @returns {Promise} - API response with success status and subscriber ID
   */
  const addToList3AndSendWelcome = async (email, attributes = {}) => {
    try {
      console.log(`🔄 [LISTMONK] Adding ${email} to List 3 and sending welcome email...`);
      
      // First, check if subscriber exists
      const { data: existingData } = await client.get(`subscribers`, {
        params: { query: `email='${email}'` }
      });
      
      const subscriber = existingData?.data?.results?.[0];
      let subscriberId;
      
      if (subscriber) {
        subscriberId = subscriber.id;
        // Update existing subscriber
        await client.put(`subscribers/${subscriberId}`, {
          email,
          name: attributes.name || '',
          attribs: {
            ...subscriber.attribs,
            ...attributes,
            email_confirmed: true,
            updated_at: new Date().toISOString()
          },
          lists: { add: [3] }
        });
        console.log(`✅ [LISTMONK] Updated existing subscriber ${subscriberId} and added to List 3`);
      } else {
        // Create new subscriber
        const { data: newSubscriber } = await client.post('subscribers', {
          email,
          name: attributes.name || '',
          attribs: {
            ...attributes,
            email_confirmed: true,
            created_at: new Date().toISOString()
          },
          lists: [3],
          status: 'enabled',
          preconfirm_subscription: true
        });
        subscriberId = newSubscriber.data.id;
        console.log(`✅ [LISTMONK] Created new subscriber ${subscriberId} in List 3`);
      }
      
      // Send welcome email using Template 4
      if (subscriberId) {
        await client.post('tx', {
          subscriber_emails: [email],  // ListMonk expects an array of emails
          template_id: parseInt(envConfig.LISTMONK_WELCOME_TEMPLATE_ID, 10),
          data: { 
            name: attributes.name || '',
            email: email,
            // Add any template-specific data
            signup_date: new Date().toISOString(),
            list_name: 'Campers'
          }
        });
        console.log(`✅ [LISTMONK] Successfully sent welcome email (Template ${envConfig.LISTMONK_WELCOME_TEMPLATE_ID}) to ${email}`);
      }
      
      return { success: true, subscriberId };
    } catch (error) {
      console.error('Error in addToList3AndSendWelcome:', error);
      throw error;
    }
  };

  /**
   * Subscribe a user to a list
   * @param {Object} params - Subscription parameters
   * @param {string} params.email - User's email address
   * @param {string} params.name - User's name (optional)
   * @param {Array<number>} params.lists - Array of list IDs to subscribe to
   * @param {Object} params.attributes - Additional user attributes (optional)
   * @returns {Promise} - API response
   */
  const subscribe = async (params) => {
    try {
      const { email, name = '', lists, attributes = {}, status = 'enabled' } = params;
      
      console.log('Subscribe called with attributes:', attributes);
      
      // Convert list IDs to integers as the Listmonk API expects integers
      const convertedLists = Array.isArray(lists) 
        ? lists.map(id => parseInt(id, 10)) 
        : [parseInt(lists, 10)];
      
      // Format the attributes for ListMonk v4+
      // Make sure all values are strings, numbers, or booleans (not objects)
      const processedAttributes = {};
      
      // Add test attribute to verify attribute storage is working
      processedAttributes.test_timestamp = new Date().toISOString();
      
      Object.keys(attributes).forEach(key => {
        const val = attributes[key];
        if (val === null || val === undefined) {
          // Skip null/undefined values
          return;
        } else if (typeof val === 'object') {
          processedAttributes[key] = JSON.stringify(val);
        } else {
          processedAttributes[key] = val;
        }
      });
      
      const payload = {
        email: email.trim().toLowerCase(), // Ensure email is properly formatted
        name,
        lists: convertedLists,
        status,
        preconfirm_subscription: true
      };
      
      // For ListMonk v4.1.0, use 'attribs' not 'attributes'
      if (Object.keys(processedAttributes).length > 0) {
        payload.attribs = processedAttributes;
      }

      console.log('Sending subscription request to Listmonk:', {
        endpoint: '/subscribers',
        lists: convertedLists,
        email: email.substring(0, 3) + '...',
        attributeCount: Object.keys(processedAttributes).length,
        payload: JSON.stringify(payload)
      });

      const response = await client.post('/subscribers', payload);
      console.log('Subscription response:', {
        success: true,
        attributes: response.data?.attribs || {},
        status: response.data?.status
      });
      
      // Check if subscriber qualifies for list number three based on attributes
      if (response.data && response.data.id && attributes && Object.keys(attributes).length > 0) {
        await evaluateAttributesForListMembership(
          response.data.id,
          attributes,
          email
        );
      }
      
      return response.data;
    } catch (error) {
      // Handle 409 conflicts as an expected case
      if (error.response?.status === 409) {
        const { email, name, lists, attributes, status } = params;
        console.log('User already exists in Listmonk, updating attributes and lists');
        
        try {
          // Get the existing subscriber to find their ID
          const { data: subscriber } = await getSubscriber(email);
          
          if (subscriber && subscriber.id) {
            console.log(`Found existing subscriber with ID: ${subscriber.id}`);
            
            // Convert list IDs again since we're in a new scope
            const convertedLists = Array.isArray(lists) 
              ? lists.map(id => parseInt(id, 10)) 
              : [parseInt(lists, 10)];
            
            // Update the existing subscriber with new attributes and lists
            const updateResult = await updateSubscriber({
              id: subscriber.id,
              name,
              lists: convertedLists,
              attributes,
              status,
              email: subscriber.email
            });
            
            return {
              status: 'updated',
              message: 'User already existed and was updated',
              data: updateResult
            };
          } else {
            console.error('Failed to find subscriber ID for update');
            return {
              status: 'exists',
              message: 'User already exists but could not be updated'
            };
          }
        } catch (updateError) {
          console.error('Error updating existing subscriber:', updateError);
          return {
            status: 'exists',
            message: 'User already exists'
          };
        }
      }
      
      // Log other errors as actual errors
      console.error('Unexpected error in Listmonk subscription:', error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Evaluate a subscriber's attributes and add them to appropriate lists based on criteria
   * @param {number} subscriberId - The subscriber's ID
   * @param {Object} attributes - The subscriber's attributes
   * @returns {Promise} - Result of any list subscriptions
   */
  const evaluateAttributesForListMembership = async (subscriberId, attributes, email) => {
    try {
      console.log(`Evaluating attributes for subscriber ${subscriberId} for list membership`);
      
      // List number three ID - replace with your actual list ID
      const targetListId = 3; // List number three
      
      // Define your criteria for automatic addition to list three
      // This is just an example - modify based on your actual requirements
      const shouldAddToListThree = 
        attributes.email_confirmed === true || 
        attributes.user_role === 'subscriber' ||
        attributes.supabase_id; // If a supabase_id exists
      
      if (shouldAddToListThree) {
        console.log(`AUTOMATIC LIST SUBSCRIPTION: Subscriber ${subscriberId} (${email}) qualifies for list #${targetListId}`);
        console.log(`Qualifying attributes:`, JSON.stringify({
          email_confirmed: attributes.email_confirmed,
          user_role: attributes.user_role,
          has_supabase_id: Boolean(attributes.supabase_id)
        }));
        
        // Add to list
        const payload = {
          email: email, // Email is required for ListMonk updates
          lists: { add: [targetListId] }
        };
        
        const response = await client.put(`/subscribers/${subscriberId}`, payload);
        console.log(`SUCCESS: Added subscriber ${subscriberId} to list #${targetListId}:`, response.data);
        return response.data;
      } else {
        console.log(`Subscriber ${subscriberId} does not qualify for list #${targetListId}`);
        console.log(`Current attributes:`, JSON.stringify(attributes));
        return null;
      }
    } catch (error) {
      console.error(`Error evaluating subscriber ${subscriberId} for list membership:`, error.message);
      return null;
    }
  };

  /**
   * Update an existing subscriber
   * @param {Object} params - Update parameters 
   * @param {number} params.id - Subscriber ID
   * @param {string} params.name - Name (optional)
   * @param {Array<number>} params.lists - List IDs (optional)
   * @param {Object} params.attributes - Attributes to update (optional)
   * @param {string} params.status - Subscriber status (optional)
   * @param {string} params.email - Subscriber email (optional)
   * @returns {Promise} - API response
   */
  const updateSubscriber = async ({ id, name, lists, attributes, status, email }) => {
    try {
      console.log(`Updating subscriber ${id} with attributes:`, attributes);
      
      // First try to get current subscriber data to ensure we have email
      let subscriber = null;
      try {
        const response = await client.get(`/subscribers/${id}`);
        subscriber = response.data;
        console.log('Current subscriber data:', {
          id: subscriber.id,
          email: subscriber.email,
          hasAttribs: !!subscriber.attribs,
          attribsCount: Object.keys(subscriber.attribs || {}).length
        });
      } catch (error) {
        console.log('Could not get current subscriber data:', error.message);
      }
      
      // Prepare a clean payload with only the fields we want to update
      const payload = {};
      
      // IMPORTANT: Always include email - ListMonk requires this
      if (email) {
        payload.email = email;
      } else if (subscriber && subscriber.email) {
        payload.email = subscriber.email;
      } else {
        throw new Error("Email is required for subscriber updates");
      }
      
      // Only add fields if they're provided
      if (name !== undefined) {
        payload.name = name;
      }
      
      if (status !== undefined) {
        payload.status = status;
      }
      
      if (lists && lists.length > 0) {
        payload.lists = { add: lists };
      }
      
      // Format attributes properly for ListMonk v4.1.0 (using attribs)
      if (attributes && Object.keys(attributes).length > 0) {
        // Process attributes to ensure proper format
        const processedAttributes = {};
        Object.keys(attributes).forEach(key => {
          const val = attributes[key];
          if (typeof val === 'object' && val !== null) {
            // Convert objects to strings for storage
            processedAttributes[key] = JSON.stringify(val);
          } else {
            processedAttributes[key] = val;
          }
        });
        
        // For ListMonk v4.1.0, use 'attribs' instead of 'attributes'
        payload.attribs = processedAttributes;
      }
      
      console.log('Sending update payload:', payload);
      
      // Send PUT request to update subscriber
      const response = await client.put(`/subscribers/${id}`, payload);
      console.log('Update response:', response.data);
      
      // After updating attributes, evaluate for list membership
      if (attributes && Object.keys(attributes).length > 0) {
        await evaluateAttributesForListMembership(
          id, 
          attributes,
          payload.email
        );
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating subscriber:', error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Get all available lists 
   * @returns {Promise} - API response with available lists
   */
  const getLists = async () => {
    try {
      console.log('Fetching lists from Listmonk');
      const response = await client.get('/lists');
      return response.data;
    } catch (error) {
      console.error('Error fetching Listmonk lists:', error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Send a transactional email
   * @param {Object} params - Email parameters
   * @param {string} params.templateId - ID of the template to use
   * @param {string} params.email - Recipient email
   * @param {Object} params.data - Data to populate the template
   * @param {string} params.fromEmail - Optional sender email address override
   * @returns {Promise} - API response
   */
  const sendTransactionalEmail = async (params) => {
    try {
      const { templateId, email, data, fromEmail } = params;
      
      // Update payload to use proper format expected by Listmonk API
      const payload = {
        template_id: parseInt(templateId, 10),
        subscriber_emails: [email], // Provide email in the format Listmonk expects
        data: {
          // Still include subscriber_email for backward compatibility
          subscriber_email: email,
          ...data
        }
      };

      // Add from_email if specified
      if (fromEmail) {
        payload.from_email = fromEmail;
      }

      console.log('Sending transactional email with Listmonk:', {
        endpoint: '/tx',
        templateId,
        email: email.substring(0, 3) + '...', // Log partial email for privacy
        fromEmail: fromEmail || 'Using template default'
      });
      
      // Debug log the full payload
      console.log('Payload for transactional email:', JSON.stringify(payload, null, 2));

      // Use the proper endpoint according to Listmonk documentation
      const response = await client.post('/tx', payload);
      return response.data;
    } catch (error) {
      console.error('Error sending transactional email:', error.response?.data || error.message);
      console.error('Error response:', error.response?.data);
      console.error('Request payload:', error.config?.data);
      
      // If we get a 404, the endpoint might be wrong
      if (error.response?.status === 404) {
        console.error('API endpoint not found. Check if the Listmonk API path is correct.');
      }
      
      throw error;
    }
  };

  /**
   * Get a subscriber by email
   * @param {string} email - The email address to look up
   * @returns {Promise} - API response with subscriber data if found
   */
  const getSubscriber = async (email) => {
    try {
      const cleanEmail = email.trim().toLowerCase();
      console.log(`Fetching subscriber info for ${cleanEmail.substring(0, 3)}...`);
      
      // Use the query endpoint first (most reliable)
      const response = await client.get('/subscribers', {
        params: {
          query: `email='${cleanEmail}'`
        }
      });
      
      console.log('Subscriber search response structure:', {
        hasData: !!response.data,
        hasDataResults: !!(response.data?.data?.results),
        hasResults: !!(response.data?.results),
        resultCount: response.data?.data?.results?.length || response.data?.results?.length || 0
      });
      
      // Extract subscriber based on response structure
      let subscriber = null;
      
      // Handle different response structures
      if (response.data?.data?.results?.length > 0) {
        subscriber = response.data.data.results[0];
      } else if (response.data?.results?.length > 0) {
        subscriber = response.data.results[0];
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        subscriber = response.data[0];
      } else if (response.data?.data?.id || response.data?.id) {
        subscriber = response.data.data || response.data;
      }
      
      // Log what we found (or didn't find)
      console.log('Subscriber search result:', 
        subscriber ? `Found: ${subscriber.id} (${subscriber.email})` : 'Not found');
      
      if (subscriber) {
        // Check for both attribute field names (attributes and attribs)
        console.log('Subscriber attribute information:', {
          hasAttributes: !!subscriber.attributes,
          hasAttribs: !!subscriber.attribs,
          attributeCount: Object.keys(subscriber.attributes || {}).length,
          attribsCount: Object.keys(subscriber.attribs || {}).length
        });
      }
      
      return {
        data: subscriber,
        exists: Boolean(subscriber)
      };
    } catch (error) {
      console.error('Error fetching subscriber:', error.response?.data || error.message);
      // On error, assume subscriber might not exist
      return {
        data: null,
        exists: false,
        error: error.message
      };
    }
  };

  /**
   * Register an unconfirmed user to ListMonk
   * @param {string} email - User's email address
   * @param {Object} attributes - Additional user attributes (optional)
   * @returns {Promise} - API response with success status and subscriber ID
   */
  const registerUnconfirmedUser = async (email, attributes = {}) => {
    try {
      console.log(`🔄 [LISTMONK] Registering unconfirmed user ${email} to ListMonk...`);
      
      // First, check if subscriber exists
      const { data: existingData } = await client.get(`subscribers`, {
        params: { query: `email='${email}'` }
      });
      
      const subscriber = existingData?.data?.results?.[0];
      let subscriberId;
      
      if (subscriber) {
        subscriberId = subscriber.id;
        console.log(`✅ [LISTMONK] User ${email} already exists in ListMonk, ID: ${subscriberId}`);
        // Don't update confirmation status if they already exist
        return { success: true, subscriberId, exists: true };
      } else {
        // Create new subscriber
        const { data: newSubscriber } = await client.post('subscribers', {
          email,
          name: attributes.name || '',
          attribs: {
            ...attributes,
            confirmation_status: 'unconfirmed',
            created_at: new Date().toISOString()
          },
          lists: [parseInt(envConfig.LISTMONK_WELCOME_LIST_ID, 10)],
          status: 'enabled',
          preconfirm_subscription: true
        });
        subscriberId = newSubscriber.data.id;
        console.log(`✅ [LISTMONK] Created new unconfirmed subscriber ${subscriberId}`);
        
        return { success: true, subscriberId, exists: false };
      }
    } catch (error) {
      console.error('Error in registerUnconfirmedUser:', error);
      throw error;
    }
  };

  /**
   * Update a user's confirmation status in ListMonk
   * @param {string} email - User's email address 
   * @returns {Promise} - API response
   */
  const updateConfirmationStatus = async (email) => {
    try {
      console.log(`🔄 [LISTMONK] Updating confirmation status for ${email}...`);
      
      // First, check if subscriber exists
      const { data: existingData } = await client.get(`subscribers`, {
        params: { query: `email='${email}'` }
      });
      
      const subscriber = existingData?.data?.results?.[0];
      
      if (!subscriber) {
        console.log(`⚠️ [LISTMONK] Subscriber ${email} not found for confirmation update`);
        return { success: false, message: 'Subscriber not found' };
      }
      
      // Get current list memberships to preserve them
      const currentListIds = subscriber.lists.map(list => list.id);
      console.log(`📋 [LISTMONK] Preserving list memberships for ${email}: ${currentListIds.join(', ')}`);
      
      // Update confirmation status while preserving list memberships
      await client.put(`subscribers/${subscriber.id}`, {
        email,
        name: subscriber.name,
        attribs: {
          ...subscriber.attribs,
          confirmation_status: 'confirmed',
          confirmed_at: new Date().toISOString()
        },
        lists: currentListIds, // Preserve current list memberships
        status: subscriber.status // Preserve current status
      });
      
      console.log(`✅ [LISTMONK] Successfully updated confirmation status for ${email}`);
      return { success: true };
    } catch (error) {
      console.error('Error updating confirmation status:', error);
      throw error;
    }
  };

  /**
   * Update a subscriber's attributes
   * @param {string} email - The subscriber's email
   * @param {Object} attributes - The attributes to update
   * @returns {Promise} - Result of the update operation
   */
  const updateSubscriberByEmail = async (email, attributes = {}) => {
    try {
      // First get the subscriber to get their ID
      const { data: subscriber, exists } = await getSubscriber(email);
      if (!exists || !subscriber) {
        console.error(`Cannot update subscriber ${email} - not found`);
        return { success: false, message: 'Subscriber not found' };
      }
      
      // Now update their attributes
      console.log(`Updating attributes for subscriber ${email} (ID: ${subscriber.id})`, attributes);
      
      return await updateSubscriber({
        id: subscriber.id,
        email: email,
        attributes: attributes
      });
    } catch (error) {
      console.error(`Error updating subscriber ${email}:`, error);
      throw error;
    }
  };

  return {
    getSubscriber,
    subscribe,
    updateSubscriber,
    updateSubscriberByEmail,
    getLists,
    sendTransactionalEmail,
    addToList3AndSendWelcome,
    registerUnconfirmedUser,
    updateConfirmationStatus
  };
};

export const listmonkClient = createListmonkClient(); 